import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Palette, RotateCcw, Save } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { adminApi } from "../../lib/adminApi";
import { normalizeDetail } from "../../lib/errors";
import { useBranding } from "../../context/BrandingContext";
import { useAdminAuth } from "../../context/AdminAuthContext";

/**
 * Admin branding editor (P1 · Task C — 08-Feb-2026). Superadmin-only.
 *
 * Full-replace PUT: the form always sends the complete branding object, so partial saves are
 * impossible and the backend can validate every field. On successful save, `refresh()` on the
 * BrandingContext re-fetches the public endpoint so the currently-open admin session also picks
 * up the new colours immediately.
 */

// Static defaults kept at module scope so the effect's dep-array can safely include them
// without triggering "new reference on every render" — const identity is stable.
const DEFAULT_COLORS = {
  gold: "#D4AF37",
  gold_deep: "#B8912E",
  bg: "#0A0908",
  accent: "#F5D57A",
};

const DEFAULT_SOCIAL = { instagram: "", linkedin: "", youtube: "" };

function ColorPicker({ label, value, onChange, testId }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-14 cursor-pointer rounded-md border border-border bg-elevated p-1"
          data-testid={`${testId}-picker`}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-32 font-mono text-sm"
          maxLength={7}
          data-testid={`${testId}-hex`}
        />
        <div
          className="h-10 flex-1 rounded-md border border-border"
          style={{ backgroundColor: value }}
          aria-label={`Preview of ${label}`}
        />
      </div>
    </div>
  );
}

export function AdminBrandingPage() {
  const { branding, refresh } = useBranding();
  const { admin } = useAdminAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const isSuperadmin = !!admin?.is_superadmin;

  useEffect(() => {
    // Prefer the admin-side GET (returns same shape but a different endpoint kept for symmetry
    // with the provider-settings pattern; useful if we ever add fields that aren't public).
    // `branding` intentionally excluded from the dep-array — we only want the initial hydration
    // once. Subsequent updates come through the local form state, not the context.
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/branding");
        if (cancelled) return;
        setForm({
          ...data,
          social_links: { ...DEFAULT_SOCIAL, ...(data.social_links || {}) },
          colors: { ...DEFAULT_COLORS, ...(data.colors || {}) },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't load branding."));
        setForm({ ...branding });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // `branding` is the fallback used only when the fetch fails — pulling it into the dep
    // array would re-fire the effect every time the context updates (e.g. after a save),
    // fighting the local form state. Intentional one-shot mount effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !form) {
    return <div className="mx-auto max-w-5xl p-8 text-sm text-muted">Loading branding…</div>;
  }

  const set = (key) => (val) => setForm((prev) => ({ ...prev, [key]: val }));
  const setColor = (key) => (val) =>
    setForm((prev) => ({ ...prev, colors: { ...prev.colors, [key]: val } }));
  const setSocial = (key) => (val) =>
    setForm((prev) => ({ ...prev, social_links: { ...prev.social_links, [key]: val } }));

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        product_name: form.product_name,
        tagline: form.tagline,
        support_email: form.support_email,
        support_whatsapp: form.support_whatsapp || "",
        footer_text: form.footer_text,
        social_links: form.social_links,
        colors: form.colors,
        email_from_name: form.email_from_name,
        otp_subject: form.otp_subject,
        otp_header: form.otp_header,
      };
      await adminApi.put("/admin/branding", payload);
      toast.success("Branding updated. All users see the new brand on their next page load.");
      await refresh();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't save branding."));
    } finally {
      setSaving(false);
    }
  }

  function handleResetColors() {
    setForm((prev) => ({ ...prev, colors: { ...DEFAULT_COLORS } }));
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="admin-branding-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-gold">Master settings</p>
        <h1 className="mt-1 text-3xl font-semibold text-text">Branding</h1>
        <p className="mt-1 text-sm text-muted">
          Change what everyone sees — product name, tagline, colours, support contacts. Applies
          instantly on the next page load for all users.
        </p>
        {!isSuperadmin && (
          <div className="mt-3 rounded-md border border-amber-400/40 bg-amber-400/10 p-3 text-sm text-amber-300">
            You're not a superadmin — the form is visible but Save is disabled.
          </div>
        )}
      </div>

      {/* Identity */}
      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">Brand identity</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label>Product name</Label>
            <Input value={form.product_name} onChange={(e) => set("product_name")(e.target.value)} data-testid="branding-product-name" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Tagline</Label>
            <Input value={form.tagline} onChange={(e) => set("tagline")(e.target.value)} data-testid="branding-tagline" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Support email</Label>
            <Input type="email" value={form.support_email} onChange={(e) => set("support_email")(e.target.value)} data-testid="branding-support-email" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Support WhatsApp (optional)</Label>
            <Input value={form.support_whatsapp} onChange={(e) => set("support_whatsapp")(e.target.value)} placeholder="+91 90000 00000" data-testid="branding-support-whatsapp" />
          </div>
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <Label>Footer text</Label>
            <Input value={form.footer_text} onChange={(e) => set("footer_text")(e.target.value)} data-testid="branding-footer" />
          </div>
        </CardContent>
      </Card>

      {/* Colours */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-gold" />
            <CardTitle className="text-base">Colour palette</CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={handleResetColors} disabled={saving} data-testid="branding-reset-colors">
            <RotateCcw className="mr-1 h-3 w-3" /> Reset to defaults
          </Button>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <ColorPicker label="Primary gold" value={form.colors.gold} onChange={setColor("gold")} testId="branding-color-gold" />
          <ColorPicker label="Gold (deep / hover)" value={form.colors.gold_deep} onChange={setColor("gold_deep")} testId="branding-color-gold-deep" />
          <ColorPicker label="Background (page)" value={form.colors.bg} onChange={setColor("bg")} testId="branding-color-bg" />
          <ColorPicker label="Accent (CTA)" value={form.colors.accent} onChange={setColor("accent")} testId="branding-color-accent" />
          <div className="rounded-md border border-border bg-elevated/50 p-3 text-xs text-muted md:col-span-2">
            <strong className="text-text">Preview:</strong> the gold is applied everywhere you see gold accents right now — try changing it and hit Save to see the entire UI shift.
          </div>
        </CardContent>
      </Card>

      {/* Social */}
      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">Social links (optional)</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="flex flex-col gap-1.5">
            <Label>Instagram</Label>
            <Input value={form.social_links.instagram} onChange={(e) => setSocial("instagram")(e.target.value)} placeholder="https://instagram.com/…" data-testid="branding-social-instagram" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>LinkedIn</Label>
            <Input value={form.social_links.linkedin} onChange={(e) => setSocial("linkedin")(e.target.value)} placeholder="https://linkedin.com/company/…" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>YouTube</Label>
            <Input value={form.social_links.youtube} onChange={(e) => setSocial("youtube")(e.target.value)} placeholder="https://youtube.com/@…" />
          </div>
        </CardContent>
      </Card>

      {/* Email defaults */}
      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">Outbound email defaults</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label>From name (fallback)</Label>
            <Input value={form.email_from_name} onChange={(e) => set("email_from_name")(e.target.value)} data-testid="branding-email-from-name" />
            <p className="text-xs text-muted">Used when the ZeptoMail card doesn't override it.</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>OTP email subject</Label>
            <Input value={form.otp_subject} onChange={(e) => set("otp_subject")(e.target.value)} data-testid="branding-otp-subject" />
          </div>
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <Label>OTP email body header</Label>
            <Input value={form.otp_header} onChange={(e) => set("otp_header")(e.target.value)} data-testid="branding-otp-header" />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button onClick={handleSave} disabled={saving || !isSuperadmin} data-testid="branding-save-btn" className="gap-1.5">
          <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save branding"}
        </Button>
      </div>
    </div>
  );
}

export default AdminBrandingPage;
